import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CustomAlert } from 'components/AlertsCollection';
import {
  selectChart,
  selectDate,
  selectDisplayMode,
  selectRuntime,
  setDisplayMode,
  setGifIndex,
  setGifs,
  setGifsAreLoading,
} from 'redux/reducers/synopticChartsReducer';
import { useLazyGetGifsQuery } from 'services/api/Meteorology/synopticCharts';
import { DisplayMode } from 'helper/products/synopticCharts';

const DEFAULT_ALERT: CustomAlert = {
  title: 'Indisponível',
  message: 'Não há nenhum GIF disponível para esta data.',
  variant: 'warning',
};

const fetchGif = async (gifUrl: string) => {
  const res = await fetch(gifUrl);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
};

export default function useGetGifs() {
  const dispatch = useDispatch();
  const [isFetching, setIsFetching] = useState(false);
  const [trigger, { data: gifUrls, error, isFetching: urlsAreLoading }] = useLazyGetGifsQuery();
  const chart = useSelector(selectChart);
  const dateString = useSelector(selectDate);
  const runtime = useSelector(selectRuntime);
  const displayMode = useSelector(selectDisplayMode);
  const fetchGeneration = useRef(0);

  const alert = error ? DEFAULT_ALERT : undefined;

  // Fallback: quando o item selecionado não tem GIFs (lista vazia ou erro), troca
  // automaticamente para o modo Imagens. Sem isso, o Map tenta renderizar
  // gifData[0] (undefined) e nada aparece até o usuário clicar no slider — o
  // que acionaria handleEnter (setDisplayMode(Images)).
  useEffect(() => {
    const hasNoGifs = !!error || (Array.isArray(gifUrls) && gifUrls.length === 0);
    if (hasNoGifs && displayMode === DisplayMode.Gif) {
      dispatch(setDisplayMode(DisplayMode.Images));
    }
  }, [error, gifUrls, displayMode, dispatch]);

  useEffect(() => {
    if (chart && dateString && runtime) {
      fetchGeneration.current += 1;
      dispatch(setGifIndex(0));
      dispatch(setGifs(undefined));
      dispatch(setGifsAreLoading(true));
      trigger({ chartId: chart.id, dateString, runtimeId: runtime.id });
    }
  }, [chart, dateString, runtime, trigger, dispatch]);

  useEffect(() => {
    if (gifUrls) {
      setIsFetching(true);
      const generation = fetchGeneration.current;
      const fetchGifs = async () => {
        const promises = gifUrls.map((gifUrl) => fetchGif(gifUrl));
        const fetchedGifs = await Promise.all(promises);
        return fetchedGifs;
      };

      fetchGifs()
        .then((g) => {
          if (fetchGeneration.current === generation) {
            dispatch(setGifs(g));
          }
        })
        .finally(() => {
          if (fetchGeneration.current === generation) {
            setIsFetching(false);
          }
        });
    }
  }, [gifUrls, dispatch]);

  useEffect(() => {
    dispatch(setGifsAreLoading(urlsAreLoading || isFetching));
  }, [urlsAreLoading, isFetching, dispatch]);

  return { alert };
}
